import { dataLayerPush } from "@/components/ProductList/v3/analytics/analytics"
import {
  addToCompareProduct,
  redirectToPdp,
  removeFromCompareProduct,
} from "@/utils/product"
import { useTranslation } from "next-i18next"
import { getPresetUrl } from "@/utils/index"
import useWindowResize from "@/hooks/useWindowResize"
import MissingImage from "@/public/images/image-missing.png"
import { useSelector } from "react-redux"
import useProductFavorites from "@/hooks/useProductFavorites"

const useProductComparison = () => {
  const { t } = useTranslation()
  const [width] = useWindowResize()
  const cartData = useSelector(state => state.cart.data)
  const { getFavIconStatus, handleFavorite, handleLogin } =
    useProductFavorites()
  /**
   * Handles compare checkbox changes
   * @param {boolean} checked - New checkbox state
   * @param {Function} setIsChecked - State setter for isChecked
   * @param {Function} setInitialLoad - State setter for initialLoad
   * @param {Object} propsProductCard - Product card props
   * @param {number} index - Product index
   * @param {String} locale - Product locale
   * @param {String} skuPrepend - SKU prepend string
   * @param {Object} general - General config information
   * @param {Object} updateProductCard - Function to update the product card
   */
  const handleCompare = (
    checked,
    setIsChecked,
    setInitialLoad,
    propsProductCard,
    index,
    locale,
    skuPrepend,
    general
  ) => {
    setIsChecked(checked)
    setInitialLoad(false)
    dataLayerPush(
      t("kf.compare.label.compare"),
      checked ? "add" : "remove",
      "compare",
      checked,
      propsProductCard,
      index,
      cartData,
      locale,
      skuPrepend,
      general
    )
  }

  const updateCompare = (
    isChecked,
    {
      propsProductCard,
      comparedProducts,
      productCategory,
      setCompareErrorMessage,
      setIsChecked,
      productSavedToList,
      userListItemsSkusRef,
      favoritesRef,
      masterSKU,
      productDataId,
      colorSwatches,
      compareErrorMessage,
      setUpdatedProductCard,
      setInitialLoad,
      index,
      setLoginFavouriteCallback,
      setOpenModal,
      setDeleteFromList,
      setShowAddToList,
      toasterContent,
      general,
      presetConf,
      plpServletPath,
      plpLocalState,
      isSaveProductToList,
      isAuthenticated,
      locale,
      skuPrepend,
      updatedProductCard,
    }
  ) => {
    const product = propsProductCard.originaldata
    const sku = propsProductCard.product.sku
    let isAllowed = false
    if (isChecked) {
      let variantFlag = false
      if (comparedProducts.length < 4) {
        const allVariantSkus = comparedProducts
          ?.map(a => {
            const variantSkus = a["variantList.sku_ss"] || []
            return [...variantSkus, a.masterSKU_s || a.sku_s].filter(Boolean)
          })
          .flat()

        if (allVariantSkus?.includes(sku)) {
          variantFlag = true
        }
        if (
          productCategory !== "" &&
          productCategory !== product?.Product_Category
        ) {
          // Product category mismatch - show error message and remove from compare
          setCompareErrorMessage(t("kf.compare.validation.sameCategory"))
          setIsChecked(false)
          removeFromCompareProduct(sku)
        } else {
          isAllowed = true
          const checkExist = comparedProducts.some(
            prod => prod.compareSku === sku
          )
          if (!checkExist) addToCompareProduct(sku, product)
        }
      } else if (
        variantFlag &&
        process.env.NEXT_PUBLIC_COMPARE_PRODUCTS_VERSION ===
          "compare_products_v2"
      ) {
        // Variant already exists in compare list
        setCompareErrorMessage(t("kf.compare.validation.sameVariants"))
        setIsChecked(false)
        removeFromCompareProduct(sku)
      } else {
        // Max products limit reached
        setCompareErrorMessage(t("kf.plp.error.maxProduct"))
        setIsChecked(false)
        removeFromCompareProduct(sku)
      }
    } else {
      // Unchecking product
      removeFromCompareProduct(sku)
    }

    const variantImages = propsProductCard.variantImages?.filter(
      img => propsProductCard.product.sku === img?.split("|")[0]
    )
    let imgUrl = propsProductCard.image?.src || ""
    if (variantImages) {
      imgUrl = getPresetUrl(
        width,
        width <= 1024 ? 2 : 3,
        general.swatchUrl,
        variantImages[0]?.split("|")[1],
        presetConf
      )
    }

    const updatedCard = {
      ...propsProductCard,
      ...updatedProductCard,
      image: {
        ...propsProductCard?.image,
        src: imgUrl,
        onError: e => {
          e.currentTarget.onerror = null
          e.currentTarget.src = [MissingImage][0].src
        },
        onClick: e => {
          redirectToPdp(
            e,
            propsProductCard.product.category,
            propsProductCard.product.slug,
            propsProductCard.product.sku,
            plpServletPath,
            general
          )
        },
      },
      isSaved: getFavIconStatus(
        isSaveProductToList,
        productSavedToList,
        userListItemsSkusRef,
        favoritesRef,
        masterSKU,
        productDataId,
        null
      ),
      saveCTA: {
        onClick: e => {
          if (isAuthenticated) {
            handleFavorite(e, {
              favoritesRef,
              userListItemsSkusRef,
              productDataId,
              masterSKU,
              setOpenModal,
              setDeleteFromList,
              setShowAddToList,
              setLoginFavouriteCallback,
              toasterContent,
              propsProductCard,
              cartData,
              isChecked,
              index,
              locale,
              skuPrepend,
              general,
              isSaveProductToList,
              isAuthenticated,
            })
          } else {
            handleLogin(
              e,
              cartData,
              propsProductCard,
              setLoginFavouriteCallback,
              locale,
              skuPrepend,
              general
            )
          }
        },
      },
      productDetails: {
        ...propsProductCard.productDetails,
        ...updatedProductCard.productDetails,
        colorSwatches,
        ...(width > 1023 && {
          checkbox: {
            ...propsProductCard.productDetails?.checkbox,
            label: t("kf.compare.label.compare"),
            defaultChecked: isAllowed,
            checked: isAllowed,
            onClick: e => {
              handleCompare(
                e.target.checked,
                setIsChecked,
                setInitialLoad,
                propsProductCard,
                index,
                locale,
                skuPrepend,
                general
              ) // pass product data and modied responseprocesser
            },
          },
        }),
      },
    }
    if (compareErrorMessage) {
      updatedCard.productDetails.checkbox.tooltip = {
        label: compareErrorMessage,
        width: "200px",
      }
    }
    setUpdatedProductCard(updatedCard)
  }
  return { handleCompare, updateCompare }
}

export default useProductComparison
